import { FC, ReactNode, useMemo, useState } from "react";
import { useQuery } from "react-query";

import LockIcon from "@material-ui/icons/Lock";
import PublicIcon from "@material-ui/icons/Public";
import StarIcon from "@material-ui/icons/Star";
import { Dropdown, Menu, MenuProps } from "antd";
import { EvaSource } from "constants/charts.enums";
import styled from "styled-components";

import { USER_DATA_SOURCES_QUERY_KEY, fetchUserDataSources } from "api/dataSource";

import SourceMenuItem from "./SourceMenuItem";

// Sentinel key for "no specific-source override — use default data source"
const DEFAULT_DATA_SOURCE_KEY = "__default_data_source__";

// Defined at module scope so the array reference is stable across renders.
const PRIVATE_HYBRID_ITEMS: MenuProps["items"] = [
  {
    key: EvaSource.Private,
    icon: <LockIcon className="source-icon" />,
    label: "Private Source"
  },
  {
    key: EvaSource.Hybrid,
    icon: <StarIcon className="source-icon" />,
    label: "Hybrid Source"
  }
];

interface Props {
  /** Current high-level source mode (Public / Private / Hybrid). */
  source: EvaSource;
  /** Per-chart public-source override. undefined = default data source. gDC==0, always check != null. */
  specificPublicSource: number | undefined;
  /** Called when the user switches source mode. */
  onSourceChange: (value: EvaSource) => void;
  /** Called when the user picks a specific public source (or undefined for default data source). */
  onSpecificSourceChange: (value: number | undefined) => void;
  trigger?: ("click" | "hover" | "contextMenu")[];
  disabled?: boolean;
  children: ReactNode;
}

/**
 * ChartDataSourceSelect — single dropdown that combines high-level source mode
 * (Public / Private / Hybrid) with per-chart specific public-source selection.
 */
const ChartDataSourceSelect: FC<Props> = ({
  source,
  specificPublicSource,
  onSourceChange,
  onSpecificSourceChange,
  trigger = ["hover"],
  disabled = false,
  children
}) => {
  const [open, setOpen] = useState(false);

  const { data: dataSources = [] } = useQuery(
    USER_DATA_SOURCES_QUERY_KEY,
    fetchUserDataSources,
    {
      select: (response) => {
        if (response.ok) {
          // Return all sources — active and inactive. Inactive sources are rendered
          // as disabled items in the menu so users can see them but not select them.
          return (
            response.value.find((c) => c.category === "Production")?.dataSources ?? []
          );
        }
        return [];
      }
    }
  );

  // "All Public Sources" is the first option — means no specific-source override.
  // Inactive sources (isActive: false) are shown but disabled.
  //
  // Active state is rendered via ItemLabel (a styled ReactNode), not via antd's
  // selectedKeys or className mechanisms — both are unreliable in antd 4.x's items API.
  // Because antd renders `label` as-is in the DOM, the inline colour is always applied.
  const publicSubItems: MenuProps["items"] = useMemo(() => {
    const activeKey =
      source === EvaSource.Public
        ? specificPublicSource != null
          ? String(specificPublicSource)
          : DEFAULT_DATA_SOURCE_KEY
        : null;

    return [
      {
        key: DEFAULT_DATA_SOURCE_KEY,
        label: (
          <SourceMenuItem active={activeKey === DEFAULT_DATA_SOURCE_KEY}>
            All Public Sources
          </SourceMenuItem>
        )
      },
      ...dataSources.map((ds) => ({
        key: ds.source,
        label: (
          <SourceMenuItem active={activeKey === String(ds.source)}>
            {ds.dataSourceDisplayName ?? ds.dataSourceName}
          </SourceMenuItem>
        ),
        disabled: !ds.isActive
      }))
    ];
  }, [dataSources, source, specificPublicSource]);

  const handlePublicHeaderClick = () => {
    onSourceChange(EvaSource.Public);
    onSpecificSourceChange(undefined);
    setOpen(false);
  };

  const handlePublicSubItemClick: MenuProps["onClick"] = ({ key }) => {
    onSourceChange(EvaSource.Public);
    if (key === DEFAULT_DATA_SOURCE_KEY) {
      onSpecificSourceChange(undefined);
    } else {
      const parsed = parseInt(key, 10);
      // Guard against non-numeric keys (sentinel or corrupted data).
      // gDC == 0 is falsy but valid — use Number.isFinite, not truthiness.
      if (Number.isFinite(parsed)) {
        onSpecificSourceChange(parsed);
      }
    }
    setOpen(false);
  };

  const handlePrivateHybridClick: MenuProps["onClick"] = ({ key }) => {
    onSourceChange(key as EvaSource);
    onSpecificSourceChange(undefined);
    setOpen(false);
  };

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      trigger={trigger}
      disabled={disabled}
      dropdownRender={() => (
        <DropdownPanel>
          {/* ── Public source section ── */}
          <PublicSectionHeader
            $isActive={source === EvaSource.Public}
            onClick={handlePublicHeaderClick}>
            <PublicIcon />
            <span>Public Source</span>
          </PublicSectionHeader>

          {/* selectable={false} — active state is owned entirely by ItemLabel styling */}
          <Menu
            selectable={false}
            onClick={handlePublicSubItemClick}
            items={publicSubItems}
            style={{
              border: "none",
              boxShadow: "none",
              padding: "2px 0",
              marginLeft: "24px"
            }}
          />

          <PanelDivider />

          {/* ── Private / Hybrid ── */}
          <Menu
            selectable
            selectedKeys={source !== EvaSource.Public ? [source] : []}
            onClick={handlePrivateHybridClick}
            items={PRIVATE_HYBRID_ITEMS}
            style={{ border: "none", boxShadow: "none", padding: "2px 0" }}
          />
        </DropdownPanel>
      )}>
      {children}
    </Dropdown>
  );
};

/* ─── Styled components ──────────────────────────────────────────────────── */

const DropdownPanel = styled.div`
  background: #fff;
  border-radius: 2px;
  box-shadow: 0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12),
    0 9px 28px 8px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  min-width: 200px;

  /* Strip extra padding antd adds inside the Menu when rendered inside a custom panel */
  .ant-menu {
    background: transparent;
  }
`;

const PublicSectionHeader = styled.div<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: ${({ $isActive }) => ($isActive ? "#fff" : "var(--color-text)")};
  background: ${({ $isActive }) => ($isActive ? "var(--color-primary)" : "transparent")};
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: ${({ $isActive }) =>
      $isActive ? "var(--color-primary-hover)" : "var(--color-primary-06)"};
  }

  .MuiSvgIcon-root {
    font-size: 16px !important;
  }
`;

const PanelDivider = styled.div`
  height: 1px;
  background: rgba(0, 0, 0, 0.06);
  margin: 2px 0;
`;

export default ChartDataSourceSelect;
